import { computed, defineComponent, onMounted, reactive, ref, watch, onBeforeUnmount } from '@vue/composition-api'
import { SettingsContext } from '@/utilities/settings-context';
import Axios from 'axios';
import { alertDialogue, generalError } from '@/functions/system-modal-functions';
import { i18n } from '@/utilities/vue-i18n';
import { escapeRegExp } from 'lodash';
import { Accounts } from "@noordigitalagency/accounts-ledigajobb";
import { setAuthenticationToken, getAuthenticationToken, hasRole } from "@/functions/authentication-functions";

import Pagination from '@/components/pagination/pagination.vue';

type UserData = {
  id: string;
  email: string;
  name: string;
  emailConfirmed: boolean;
  companies: string[];
  loading: {
    resendConfirmation: boolean;
  };
}

type SearchableUserData = UserData & {
  searchEmail: string;
  searchName: string;
  searchCompanies: string;
};

interface AdminUsersComponentModel
{
  loading: boolean;

  filter: string;

  chunk: {
    startAt: number;
    endAt: number;
  };
}

export default defineComponent({
  components: {
    Pagination
  },

  setup()
  {
    const searchInput = ref<HTMLInputElement | null>(null);

    const users = ref<SearchableUserData[]>([]);

    const isAdmin = computed(() => hasRole('Administrator'));

    const currentPage = ref(0);

    const model = reactive<AdminUsersComponentModel>({
      loading: false,
      filter: '',
      chunk: { startAt: 0, endAt: 20 }
    });

    const hasFilter = computed(() => !!model.filter);

    const filterRegex = computed(() => new RegExp(escapeRegExp(model.filter), 'gi'));

    const isMatching = (filter: string) =>
      (user: SearchableUserData) =>
        user.searchName.includes(filter) === true ||
        user.searchEmail.includes(filter) === true ||
        user.searchCompanies.includes(filter) === true;

    const highLightTextIfFiltered = (text: string) => !hasFilter.value ? text : text?.replace(filterRegex.value, (match) => `<span class="highlight">${match}</span>`);

    const focusSearch = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.keyCode === 70) {
          e.preventDefault();
          searchInput.value?.focus();
      }
    };

    watch(() => model.filter, () => {
      currentPage.value = 1;
      model.chunk.startAt = 0;
      model.chunk.endAt = 20;
    });

    const filteredUsers = computed(() =>
    {
      const value = users.value;

      if (hasFilter.value)
      {
        return value.filter(isMatching(model.filter.toLowerCase()));
      }

      return value;
    });

    const initUser = (user: Omit<UserData, 'loading'>): SearchableUserData => ({
      ...user,
      searchCompanies: user.companies?.filter(x => !!x?.trim()).join('').toLowerCase() ?? '',
      searchEmail: user.email?.trim().toLowerCase() ?? '',
      searchName: user.name?.trim().toLowerCase() ?? '',
      loading: { resendConfirmation: false }
    });

    const getUsers = async () =>
    {
      try
      {
        const result = await Axios.get<UserData[]>(SettingsContext.getApiUrl('getAllUsers'));

        users.value = result.data?.map(initUser).sort((a, b) => !a.name ? 1 : a.name > b.name ? 1 : -1) ?? [];
      }
      catch {
        // Ignored
      }
    };

    const impersonate = async (user: UserData) =>
    {
      const accounts = new Accounts(SettingsContext.constants.accountsApiDomainName, getAuthenticationToken());

      setAuthenticationToken(await accounts.impersonate(user.id));

      location.pathname = '/';
    };

    const resendConfirmation = async (user: UserData) =>
    {
      user.loading.resendConfirmation = true;

      try
      {
        await Axios.post(SettingsContext.getApiUrl('resendConfirmationByEmail'), { email: user.email });

        await alertDialogue(i18n.t('dialogue.success-title') as string, i18n.t('dialogue.confirmation-email-sent') as string);
      }
      catch (error)
      {
        await generalError(false);

        console.error(error);
      }

      user.loading.resendConfirmation = false;
    };

    onMounted(async () =>
    {
      model.loading = true;

      await getUsers();

      model.loading = false;

      window.addEventListener('keydown', focusSearch);
    });

    onBeforeUnmount(() => {
      window.removeEventListener('keydown', focusSearch);
    });

    const chunkUsers = (start: number, end: number) =>
    {
      model.chunk.endAt = end;
      model.chunk.startAt = start;
    }

    return {
      searchInput,
      model,
      chunkUsers,
      currentPage,
      users: filteredUsers,
      impersonate,
      resendConfirmation,
      highLightTextIfFiltered,
      isAdmin,
    }
  },
});
